'use client'

import { motion } from 'framer-motion'

const lanes = [
  {
    label: 'Lane 1',
    title: 'Smart Real Estate',
    description:
      'Your tenants book parking, EV charging, and amenities in one tap. Your ops team stops chasing maintenance tickets. Fully white-labeled, deployed under your brand.',
    items: [
      'Parking & EV charging',
      'Amenity booking',
      'Maintenance dispatch',
      'Tenant communications',
    ],
  },
  {
    label: 'Lane 2',
    title: 'Sovereign Mobility',
    description:
      'National Mobility-as-a-Feature (MaaF) platforms built to sovereign cloud requirements. Multi-modal routing, real-time dispatch, demand forecasting. Designed for public service.',
    items: [
      'Multi-modal routing',
      'Sovereign AI clouds',
      'Real-time dispatch',
      'Demand forecasting',
    ],
  },
  {
    label: 'Lane 3',
    title: 'Vertical AI Engine',
    description:
      'Not a general-purpose AI bolted onto logistics. Built from the ground up for physical operations, with the domain depth to know that EV charger downtime costs more than the repair.',
    items: [
      'Predictive demand modeling',
      'Dynamic pricing',
      'Digital twin',
      'Proactive maintenance',
    ],
  },
]

export function UseCasesSection() {
  return (
    <section id="solutions" className="py-24 bg-[#07090f]">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <p className="text-xs font-mono tracking-widest text-[#00c8ff] uppercase mb-4">
            Three-Lane Strategy
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-[#e2e8f0] tracking-tight">
            Orchestration, Not Just Conversation
          </h2>
          <p className="mt-4 text-[#94a3b8] max-w-xl mx-auto text-lg">
            We connect a user request to a physical action. Every time.
          </p>
        </div>

        {/* Old Way / New Way contrast */}
        <div className="mb-16 max-w-xl mx-auto">
          <div className="flex justify-between mb-3 px-1 text-xs font-mono tracking-widest uppercase">
            <span className="text-[#94a3b8]">Before</span>
            <span className="text-[#00c8ff]">After</span>
          </div>
          <div className="rounded-xl border border-[#1c2333] bg-[#0d1117] divide-y divide-[#1c2333]">
            {[
              {
                before: '"We answer your phone."',
                after: '"We manage your grid."',
              },
              {
                before: '"We log the ticket."',
                after: '"We dispatch the tech."',
              },
              {
                before: '"We report the data."',
                after: '"We act on it."',
              },
            ].map((row) => (
              <div
                key={row.before}
                className="grid grid-cols-[1fr_28px_1fr] items-center gap-2 px-6 py-4"
              >
                <p className="text-sm text-[#94a3b8]">{row.before}</p>
                <span className="text-[#00c8ff] text-center text-base">→</span>
                <p className="text-sm text-[#e2e8f0] font-medium text-right">
                  {row.after}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {lanes.map((lane, i) => (
            <motion.div
              key={lane.title}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="relative p-8 rounded-xl border border-[#1c2333] bg-[#0d1117] hover:border-[#00c8ff]/30 transition-colors"
            >
              <span className="text-xs font-mono text-[#00c8ff] tracking-widest uppercase mb-4 block">
                {lane.label}
              </span>

              <h3 className="text-xl font-semibold text-[#e2e8f0] mb-3">
                {lane.title}
              </h3>
              <p className="text-base text-[#94a3b8] mb-6 leading-relaxed">
                {lane.description}
              </p>

              <ul className="space-y-2">
                {lane.items.map((item) => (
                  <li
                    key={item}
                    className="flex items-center gap-2 text-base text-[#94a3b8]"
                  >
                    <span className="w-1 h-1 rounded-full bg-[#00c8ff] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
